import { useMemo, useState, useEffect } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { AuthenticityBand } from "@talenthop/shared";
import { rankBucketLabel, rankBucketOf, type RankBucket, type RankingBasis } from "@talenthop/shared";
import { api } from "../../lib/api.js";
import { relativeActivityDate } from "../../lib/format.js";
import { RequirementCombobox } from "../../components/RequirementCombobox.js";
import { displayTitle } from "./displayTitle.js";
import { getRequisitionStatus } from "./requisitionFormat.js";
import { humaneRecommendation } from "./integrityLabels.js";
import {
  Briefcase,
  FileText,
  Search,
  ShieldCheck,
  Sparkles,
  Video,
  Filter,
  X,
  Users,
  Alert,
  ArrowLeft,
  ArrowRight,
} from "../../components/icons.js";
import { funnelStageFromParam, reachedStage } from "./funnelStages.js";
import { FunnelStageChip } from "./FunnelStageChip.js";

/**
 * Users · Interviews tab (/app/interviews).
 * Candidate voice AI interview evaluations, competency scoring, and verification proof.
 * Theme 1 layout (docs/ux-audit/theme1/interviews.html): page header, KPI cards, segmented status
 * tabs, toolbar (search, requirement, filters, sort), a collapsible filter panel and a compact
 * table that collapses into cards on a phone.
 *
 * Data: GET /org/interviews (apps/api/src/functions/orgInterviews.ts).
 */

/** The row's bucket for filters and counts — from the API when it sent one, else the shared definition. */
function bucketOf(i: InterviewItem): RankBucket {
  if (i.rankBucket) return i.rankBucket;
  return rankBucketOf({
    basis: i.rankingBasis ?? "interview",
    interview: { score: i.interviewScore, scored: i.interviewQuestionsScored ?? 0, total: i.interviewQuestionsTotal ?? 0 },
    capability: i.competencyScore ?? null,
    coverage: i.coverage ?? null,
    evaluated: (i.assessmentSetVersion ?? null) != null,
    outdated: !!i.outdated,
  });
}

export interface InterviewItem {
  candidacyId: string;
  candidateName: string | null;
  candidateEmail: string | null;
  candidatePhone: string | null;
  requisitionId: string;
  requisitionTitle: string;
  state: string;
  resumeScore: number | null;
  /** The question-level INTERVIEW score (0105): the average of each answered question's own 0-100 score. Null = not scored yet. */
  interviewScore: number | null;
  interviewQuestionsScored?: number;
  interviewQuestionsTotal?: number;
  /** Capability of the current competency evaluation (0099) — null when nothing was evidenced, or not evaluated. */
  competencyScore?: number | null;
  /** What this row's organization ranks by. */
  rankingBasis?: RankingBasis;
  /** Share of the criteria (by weight) the interview evidenced; shown beside capability, never merged. */
  coverage?: number | null;
  assessmentSetVersion?: number | null;
  /** Evaluated against an older version of the requirement's criteria — not comparable. */
  outdated?: boolean;
  /** 0100: shared headline class and ranking tier from the API (coverage- and version-aware). */
  matchClass?: string;
  rankTier?: number;
  /** Filter bucket from the shared definition (strong / moderate / low / limited_evidence / no_evidence / outdated / not_evaluated). */
  rankBucket?: RankBucket;
  /** 1 = best — the API's ranking (shared rankBy). The list renders this order; it never re-sorts by its own rule. */
  rank?: number;
  /** score null = insufficient evidence (never a 0). */
  competencies: { id: string; label: string; score: number | null; status?: "evidenced" | "insufficient_evidence" }[];
  authenticityBand: AuthenticityBand | null;
  recommendation: string | null;
  sessionMinutes: number | null;
  completedAt: string | null;
  scoredAt: string | null;
}

interface RequisitionOption {
  id: string;
  title: string;
  state: string;
  candidateCount?: number;
}

export type InterviewStatusTab = "all" | "verified" | "needs_review" | "top_tier" | "in_progress";
export type AuthenticityFilter = "all" | "verified" | "needs_review" | "failed";
export type ScoreRangeFilter = "all" | RankBucket;
export type SessionStatusFilter = "all" | "scored" | "in_progress";
export type SortOption = "score_desc" | "score_asc" | "resume_desc" | "date_desc" | "duration_desc";

const ALL = "all";
const PAGE_SIZE = 30;

const AVATAR_COLORS = ["blue", "yellow", "teal", "purple"] as const;
type AvatarColor = (typeof AVATAR_COLORS)[number];

function getAvatarColor(id: string): AvatarColor {
  const code = id.charCodeAt(0) || 0;
  return AVATAR_COLORS[code % AVATAR_COLORS.length]!;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "CD";
  return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join("");
}

const avg = (xs: (number | null)[]): number | null => {
  const v = xs.filter((x): x is number => x != null);
  return v.length ? Math.round(v.reduce((a, b) => a + b, 0) / v.length) : null;
};

const fmt = (n: number | null): string => (n == null ? "—" : `${n}%`);

export function InterviewsHub(): ReactNode {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeReqId = searchParams.get("reqId") ?? ALL;
  // Opened from a requirement's funnel card: keep only the people that card counted (funnelStages.ts).
  const funnelStage = funnelStageFromParam(searchParams.get("stage"));

  // Filter UI states
  // Filters live in a left sidebar on desktop (open by default, the Filters button hides it). On a
  // phone the same panel is a drawer: it starts closed and the Filters button slides it in.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      return !window.matchMedia("(max-width: 900px)").matches;
    } catch {
      return true;
    }
  });
  const closeDrawerOnPhone = (): void => {
    try {
      if (window.matchMedia("(max-width: 900px)").matches) setSidebarOpen(false);
    } catch {
      /* desktop: the sidebar stays */
    }
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [authenticityFilter, setAuthenticityFilter] = useState<AuthenticityFilter>("all");
  const [scoreRangeFilter, setScoreRangeFilter] = useState<ScoreRangeFilter>("all");
  const [sessionStatusFilter, setSessionStatusFilter] = useState<SessionStatusFilter>("all");
  const [selectedCompetencies, setSelectedCompetencies] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("score_desc");

  // Status Tabs Bar: All | Verified | Needs Review | Top Tier (85%+) | In Progress
  const [statusTab, setStatusTab] = useState<InterviewStatusTab>("all");

  // Clickable Sub-Filter for Scoped Metrics Banner
  const [metricSubFilter, setMetricSubFilter] = useState<
    "all" | "scored" | "verified" | "needs_review" | "top_tier"
  >("all");

  // Pagination (30 interviews per page)
  const [currentPage, setCurrentPage] = useState(1);

  // Queries
  const { data, isLoading, isError } = useQuery({
    queryKey: ["org-interviews", activeReqId],
    queryFn: () =>
      api.get<{ interviews: InterviewItem[] }>(
        activeReqId === ALL ? "/org/interviews" : `/org/interviews?requisitionId=${encodeURIComponent(activeReqId)}`,
      ),
    refetchInterval: 30_000,
  });

  const reqsQuery = useQuery({
    queryKey: ["requisitions"],
    queryFn: () => api.get<{ requisitions: RequisitionOption[] }>("/requisitions"),
    staleTime: 30_000,
  });


  const rawInterviews = useMemo(
    () => (data?.interviews ?? []).filter((i) => !funnelStage || reachedStage(i.state, funnelStage.rank)),
    [data, funnelStage?.rank],
  );
  // What this organization ranks by (every row carries it); the labels and filters follow.
  const basis: RankingBasis = rawInterviews[0]?.rankingBasis ?? data?.interviews?.[0]?.rankingBasis ?? "interview";

  // Reset pagination to page 1 whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeReqId,
    statusTab,
    searchQuery,
    authenticityFilter,
    scoreRangeFilter,
    sessionStatusFilter,
    selectedCompetencies,
    metricSubFilter,
    sortBy,
  ]);

  // Reset metric sub-filter when requisition or tab changes
  useEffect(() => {
    setMetricSubFilter("all");
  }, [activeReqId, statusTab]);

  // Extract unique competencies across all interviews for dynamic sidebar chips
  const allCompetencyLabels = useMemo(() => {
    const set = new Set<string>();
    rawInterviews.forEach((i) => {
      i.competencies.forEach((c) => {
        if (c.label && c.label.trim()) {
          set.add(c.label.trim());
        }
      });
    });
    return Array.from(set).slice(0, 15);
  }, [rawInterviews]);

  // Handle requirement selection
  function handleSelectRequirement(newReqId: string) {
    if (newReqId === ALL) {
      searchParams.delete("reqId");
      searchParams.delete("stage"); // a funnel stage belongs to one requirement's funnel
      setSearchParams(searchParams);
    } else {
      setSearchParams({ reqId: newReqId });
    }
  }

  // Toggle competency in filter
  function toggleCompetency(comp: string) {
    setSelectedCompetencies((prev) =>
      prev.includes(comp) ? prev.filter((c) => c !== comp) : [...prev, comp]
    );
  }

  // Clear all sidebar filters
  function clearAllFilters() {
    setSearchQuery("");
    setAuthenticityFilter("all");
    setScoreRangeFilter("all");
    setSessionStatusFilter("all");
    setSelectedCompetencies([]);
    setMetricSubFilter("all");
  }

  const hasActiveFilters =
    searchQuery.trim() !== "" ||
    authenticityFilter !== "all" ||
    scoreRangeFilter !== "all" ||
    sessionStatusFilter !== "all" ||
    selectedCompetencies.length > 0 ||
    metricSubFilter !== "all";

  // Compute dynamic counts for the 5 status tabs
  const tabCounts = useMemo(() => {
    const base = rawInterviews.filter((i) => {
      if (activeReqId !== ALL && i.requisitionId !== activeReqId) return false;

      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const nameMatch = (i.candidateName ?? "").toLowerCase().includes(q);
        const emailMatch = (i.candidateEmail ?? "").toLowerCase().includes(q);
        const phoneMatch = (i.candidatePhone ?? "").toLowerCase().includes(q);
        const roleMatch = (i.requisitionTitle ?? "").toLowerCase().includes(q);
        const compMatch = i.competencies.some((c) => c.label.toLowerCase().includes(q));
        if (!nameMatch && !emailMatch && !phoneMatch && !roleMatch && !compMatch) return false;
      }

      // Authenticity filter
      if (authenticityFilter === "verified" && i.authenticityBand !== "verified") return false;
      if (
        authenticityFilter === "needs_review" &&
        i.authenticityBand !== "needs_review" &&
        i.authenticityBand !== "failed"
      )
        return false;
      if (authenticityFilter === "failed" && i.authenticityBand !== "failed") return false;

      // Match filter — the shared buckets, so coverage and out-of-date evaluations can never be bypassed
      if (scoreRangeFilter !== "all" && bucketOf(i) !== scoreRangeFilter) return false;

      // Session status filter
      if (sessionStatusFilter === "scored" && i.scoredAt == null) return false;
      if (sessionStatusFilter === "in_progress" && i.scoredAt != null) return false;

      // Competency filter
      if (selectedCompetencies.length > 0) {
        const hasAllComps = selectedCompetencies.every((comp) =>
          i.competencies.some((c) => c.label.toLowerCase() === comp.toLowerCase())
        );
        if (!hasAllComps) return false;
      }

      return true;
    });

    const all = base.length;
    const verified = base.filter((i) => i.authenticityBand === "verified").length;
    const needs_review = base.filter(
      (i) => i.authenticityBand === "needs_review" || i.authenticityBand === "failed"
    ).length;
    const top_tier = base.filter((i) => bucketOf(i) === "strong").length;
    const in_progress = base.filter((i) => i.scoredAt == null).length;

    return { all, verified, needs_review, top_tier, in_progress };
  }, [
    rawInterviews,
    activeReqId,
    searchQuery,
    authenticityFilter,
    scoreRangeFilter,
    sessionStatusFilter,
    selectedCompetencies,
  ]);

  // Candidates scoped to currently selected status tab + sidebar filters
  const tabScopedInterviews = useMemo(() => {
    return rawInterviews.filter((i) => {
      // Requisition filter
      if (activeReqId !== ALL && i.requisitionId !== activeReqId) return false;

      // Status tab filter
      if (statusTab === "verified") {
        if (i.authenticityBand !== "verified") return false;
      } else if (statusTab === "needs_review") {
        if (i.authenticityBand !== "needs_review" && i.authenticityBand !== "failed") return false;
      } else if (statusTab === "top_tier") {
        if (bucketOf(i) !== "strong") return false;
      } else if (statusTab === "in_progress") {
        if (i.scoredAt != null) return false;
      }

      // Search query filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const nameMatch = (i.candidateName ?? "").toLowerCase().includes(q);
        const emailMatch = (i.candidateEmail ?? "").toLowerCase().includes(q);
        const phoneMatch = (i.candidatePhone ?? "").toLowerCase().includes(q);
        const roleMatch = (i.requisitionTitle ?? "").toLowerCase().includes(q);
        const compMatch = i.competencies.some((c) => c.label.toLowerCase().includes(q));
        if (!nameMatch && !emailMatch && !phoneMatch && !roleMatch && !compMatch) return false;
      }

      // Authenticity filter
      if (authenticityFilter === "verified" && i.authenticityBand !== "verified") return false;
      if (
        authenticityFilter === "needs_review" &&
        i.authenticityBand !== "needs_review" &&
        i.authenticityBand !== "failed"
      )
        return false;
      if (authenticityFilter === "failed" && i.authenticityBand !== "failed") return false;

      // Match filter — the shared buckets, so coverage and out-of-date evaluations can never be bypassed
      if (scoreRangeFilter !== "all" && bucketOf(i) !== scoreRangeFilter) return false;

      // Session status filter
      if (sessionStatusFilter === "scored" && i.scoredAt == null) return false;
      if (sessionStatusFilter === "in_progress" && i.scoredAt != null) return false;

      // Competency filter
      if (selectedCompetencies.length > 0) {
        const hasAllComps = selectedCompetencies.every((comp) =>
          i.competencies.some((c) => c.label.toLowerCase() === comp.toLowerCase())
        );
        if (!hasAllComps) return false;
      }

      return true;
    });
  }, [
    rawInterviews,
    activeReqId,
    statusTab,
    searchQuery,
    authenticityFilter,
    scoreRangeFilter,
    sessionStatusFilter,
    selectedCompetencies,
  ]);

  // Aggregated KPI counts for the processing summary banner
  const pipelineMetrics = useMemo(() => {
    const rows = tabScopedInterviews;
    // The average interview score over interviews that have one (it does not depend on the criteria version).
    const scoredList = rows.filter((i) => i.interviewScore != null);
    const verifiedCount = scoredList.filter((i) => i.authenticityBand === "verified").length;
    const needsReviewCount = rows.filter(
      (i) => i.authenticityBand === "needs_review" || i.authenticityBand === "failed"
    ).length;
    const topTierCount = rows.filter((i) => bucketOf(i) === "strong").length;

    return {
      total: rows.length,
      scored: scoredList.length,
      verified: verifiedCount,
      needsReview: needsReviewCount,
      topTier: topTierCount,
      avgInterview: avg(scoredList.map((i) => i.interviewScore)),
      avgResume: avg(rows.map((i) => i.resumeScore)),
    };
  }, [tabScopedInterviews]);

  // Filtered interviews after applying active metric sub-filter chip & sorting
  const filteredAndSortedInterviews = useMemo(() => {
    let list = tabScopedInterviews;

    if (metricSubFilter === "scored") {
      list = list.filter((i) => i.scoredAt != null);
    } else if (metricSubFilter === "verified") {
      list = list.filter((i) => i.authenticityBand === "verified");
    } else if (metricSubFilter === "needs_review") {
      list = list.filter((i) => i.authenticityBand === "needs_review" || i.authenticityBand === "failed");
    } else if (metricSubFilter === "top_tier") {
      list = list.filter((i) => bucketOf(i) === "strong");
    }

    return [...list].sort((a, b) => {
      // Ranking: the API's order (shared rankBy — tier, capability, coverage, deterministic tie-breaks).
      // Lowest first is the exact reverse; nothing here defines an ordering of its own (P1-9).
      const rank = (i: InterviewItem): number => i.rank ?? Number.MAX_SAFE_INTEGER;
      if (sortBy === "score_desc") return rank(a) - rank(b);
      if (sortBy === "score_asc") return rank(b) - rank(a);
      if (sortBy === "resume_desc") {
        return (b.resumeScore ?? -1) - (a.resumeScore ?? -1);
      }
      if (sortBy === "duration_desc") {
        return (b.sessionMinutes ?? -1) - (a.sessionMinutes ?? -1);
      }
      if (sortBy === "date_desc") {
        const dateA = a.completedAt || a.scoredAt || "";
        const dateB = b.completedAt || b.scoredAt || "";
        return dateB.localeCompare(dateA);
      }
      return 0;
    });
  }, [tabScopedInterviews, metricSubFilter, sortBy]);

  // Pagination calculations
  const totalInterviews = filteredAndSortedInterviews.length;
  const totalPages = Math.max(1, Math.ceil(totalInterviews / PAGE_SIZE));
  const paginatedInterviews = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredAndSortedInterviews.slice(start, start + PAGE_SIZE);
  }, [filteredAndSortedInterviews, currentPage]);

  const reqOptions = useMemo(() => {
    return (reqsQuery.data?.requisitions ?? [])
      .filter((r) => getRequisitionStatus(r.state) === "Active")
      .map((r) => ({
        id: r.id,
        title: displayTitle(r.title),
        meta: r.candidateCount ? `${r.candidateCount} candidate${r.candidateCount === 1 ? "" : "s"}` : undefined,
      }));
  }, [reqsQuery.data]);

  const backToUrl = `/app/interviews${activeReqId === ALL ? "" : `?reqId=${encodeURIComponent(activeReqId)}`}`;

  const activeFilterCount =
    (authenticityFilter !== "all" ? 1 : 0) +
    (scoreRangeFilter !== "all" ? 1 : 0) +
    (sessionStatusFilter !== "all" ? 1 : 0) +
    selectedCompetencies.length;

  const inProgressCount = pipelineMetrics.total - pipelineMetrics.scored;

  const STATUS_TABS: { key: InterviewStatusTab; label: string; count: number }[] = [
    { key: "all", label: "All", count: tabCounts.all },
    { key: "verified", label: "Verified", count: tabCounts.verified },
    { key: "needs_review", label: "Needs review", count: tabCounts.needs_review },
    { key: "top_tier", label: rankBucketLabel("strong", basis), count: tabCounts.top_tier },
    { key: "in_progress", label: "In progress", count: tabCounts.in_progress },
  ];

  const activeReqTitle =
    activeReqId === ALL ? null : (reqOptions.find((r) => r.id === activeReqId)?.title ?? null);

  return (
    <div className="t1-ih">
      {/* ── Page header: title + one-line purpose, balance chip, one primary action ── */}
      <div className="page-h t1-ih-head">
        <div style={{ minWidth: 0 }}>
          <h1>Interviews</h1>
          <div className="sub">
            Voice AI interview results — verification proof, competency scores and transcripts. Scores rank
            candidates; a human always decides.
          </div>
          {funnelStage && <FunnelStageChip label={funnelStage.label} count={rawInterviews.length} />}
        </div>
        <div className="t1-ih-head-actions">
          <Link
            to="/app/candidates"
            className="btn primary"
          >
            <Users />
            <span>Invite candidates</span>
          </Link>
        </div>
      </div>

      {/* ── KPI cards (first one dark). Verified / needs-review double as quick filters. ── */}
      <section className="t1-ih-kpis" aria-label="Interview summary">
        <button
          type="button"
          className={`t1-kpi dark ${metricSubFilter === "all" ? "" : ""}`}
          onClick={() => setMetricSubFilter("all")}
         
        >
          <span className="t1-kpi-top">
            Interviews
            <span className="t1-kpi-ico" aria-hidden="true">
              <Video />
            </span>
          </span>
          <span className="t1-kpi-val tnum">{pipelineMetrics.total}</span>
          <span className="t1-kpi-sub">
            {pipelineMetrics.scored} scored · {inProgressCount} in progress
          </span>
        </button>
        <button
          type="button"
          className={`t1-kpi ${metricSubFilter === "verified" ? "on" : ""}`}
          onClick={() => setMetricSubFilter((p) => (p === "verified" ? "all" : "verified"))}
          aria-pressed={metricSubFilter === "verified"}
         
        >
          <span className="t1-kpi-top">
            Verified
            <span className="t1-kpi-ico" aria-hidden="true">
              <ShieldCheck />
            </span>
          </span>
          <span className="t1-kpi-val tnum">{pipelineMetrics.verified}</span>
          <span className="t1-kpi-sub">
            {pipelineMetrics.needsReview > 0 ? (
              <span
                className={`pill r t1-kpi-pill ${metricSubFilter === "needs_review" ? "on" : ""}`}
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  setMetricSubFilter((p) => (p === "needs_review" ? "all" : "needs_review"));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    setMetricSubFilter((p) => (p === "needs_review" ? "all" : "needs_review"));
                  }
                }}
               
              >
                {pipelineMetrics.needsReview} need review
              </span>
            ) : (
              "None need review"
            )}
          </span>
        </button>
        <div className="t1-kpi" title="Average interview score (question-level) across scored interviews">
          <span className="t1-kpi-top">
            Avg interview score
            <span className="t1-kpi-ico" aria-hidden="true">
              <Sparkles />
            </span>
          </span>
          <span className="t1-kpi-val tnum">{fmt(pipelineMetrics.avgInterview)}</span>
          <span className="t1-kpi-sub">across {pipelineMetrics.scored} scored</span>
        </div>
        <div className="t1-kpi" title="Average resume-to-job-description match">
          <span className="t1-kpi-top">
            Avg resume match
            <span className="t1-kpi-ico" aria-hidden="true">
              <FileText />
            </span>
          </span>
          <span className="t1-kpi-val tnum">{fmt(pipelineMetrics.avgResume)}</span>
          <span className="t1-kpi-sub">scored separately from the interview</span>
        </div>
      </section>

      {/* ── Filter sidebar (left) + list card: status tabs, toolbar, compact table ── */}
      <div className={`t1-ih-layout ${sidebarOpen ? "with-side" : ""}`}>
        {/* Filters — left sidebar on desktop, a slide-in drawer on a phone. */}
        {sidebarOpen && (
          <>
          <div className="t1-ih-scrim" aria-hidden="true" onClick={() => setSidebarOpen(false)} />
          <aside id="t1-ih-filters" className="card t1-ih-filters" aria-label="Filter interviews">
            <div className="t1-ih-filters-h">
              <h2>Filters</h2>
              <button
                type="button"
                className="btn sm ghost t1-icon-btn"
                onClick={() => setSidebarOpen(false)}
                aria-label="Hide filters"
                data-tip="Hide filters"
                data-tip-pos="left"
              >
                <X />
              </button>
            </div>
            <fieldset>
              <legend>Verification / integrity</legend>
              {[
                { label: "All integrity bands", value: "all" },
                { label: "Verified (passed)", value: "verified" },
                { label: "Needs review", value: "needs_review" },
                { label: "Integrity issues", value: "failed" },
              ].map((opt) => (
                <label key={opt.value}>
                  <input
                    type="radio"
                    name="authenticityFilter"
                    checked={authenticityFilter === opt.value}
                    onChange={() => setAuthenticityFilter(opt.value as AuthenticityFilter)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </fieldset>
            <fieldset>
              <legend>{basis === "interview" ? "Answers" : "Match"}</legend>
              {[
                { label: basis === "interview" ? "All interviews" : "All matches", value: "all" },
                ...(["strong", "moderate", "low", "limited_evidence", "no_evidence", "outdated"] as RankBucket[]).map((b) => ({
                  label: rankBucketLabel(b, basis),
                  value: b,
                })),
              ].map((opt) => (
                <label key={opt.value}>
                  <input
                    type="radio"
                    name="scoreRangeFilter"
                    checked={scoreRangeFilter === opt.value}
                    onChange={() => setScoreRangeFilter(opt.value as ScoreRangeFilter)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </fieldset>
            <fieldset>
              <legend>Session status</legend>
              {[
                { label: "All sessions", value: "all" },
                { label: "Scored & completed", value: "scored" },
                { label: "In progress / scoring", value: "in_progress" },
              ].map((opt) => (
                <label key={opt.value}>
                  <input
                    type="radio"
                    name="sessionStatusFilter"
                    checked={sessionStatusFilter === opt.value}
                    onChange={() => setSessionStatusFilter(opt.value as SessionStatusFilter)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </fieldset>
            {allCompetencyLabels.length > 0 && (
              <fieldset className="t1-ih-comps-filter">
                <legend>Competencies</legend>
                <div>
                  {allCompetencyLabels.map((comp) => {
                    const isSelected = selectedCompetencies.includes(comp);
                    return (
                      <button
                        key={comp}
                        type="button"
                        className={`t1-chip ${isSelected ? "on" : ""}`}
                        aria-pressed={isSelected}
                        onClick={() => toggleCompetency(comp)}
                      >
                        {sentenceCase(comp)}
                      </button>
                    );
                  })}
                </div>
              </fieldset>
            )}
            {hasActiveFilters && (
              <div className="t1-ih-filters-foot">
                <button type="button" className="btn sm ghost" onClick={clearAllFilters}>
                  <X />
                  Clear all filters
                </button>
              </div>
            )}
          <button type="button" className="btn sm primary t1-ih-drawer-done" onClick={closeDrawerOnPhone}>
            Show {filteredAndSortedInterviews.length} result{filteredAndSortedInterviews.length === 1 ? "" : "s"}
          </button>
          </aside>
          </>
        )}

      <section className="card t1-ih-list">
        <div className="t1-ih-toolbar">
          <div className="seg t1-ih-tabs" role="tablist" aria-label="Interview status">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={statusTab === t.key}
                className={statusTab === t.key ? "on" : ""}
                onClick={() => {
                  setStatusTab(t.key);
                  setMetricSubFilter("all");
                }}
              >
                {t.label}
                <i className="tnum">{t.count}</i>
              </button>
            ))}
          </div>
          <div className="t1-ih-tools">
            <label className="t1-ih-search">
              <Search aria-hidden="true" />
              <input
                type="search"
                placeholder="Name, email, role or competency"
                aria-label="Search interviews"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </label>
            <RequirementCombobox
              className="t1-ih-req"
              options={reqOptions}
              allOption={{ id: ALL, title: `All requirements (${rawInterviews.length})` }}
              value={activeReqId}
              loading={reqsQuery.isLoading}
              onSelect={handleSelectRequirement}
            />
            <button
              type="button"
              className={`btn sm t1-ih-filter-btn ${sidebarOpen ? "t1-on" : ""}`}
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-expanded={sidebarOpen}
              aria-controls="t1-ih-filters"
              aria-label={sidebarOpen ? "Hide filters" : "Show filters"}
              data-tip={sidebarOpen ? "Hide filters" : "Show filters"}
              data-tip-pos="left"
            >
              <Filter />
              <span className="t1-ih-filter-lbl">Filters</span>
              {activeFilterCount > 0 && <span className="t1-ih-count">{activeFilterCount}</span>}
            </button>
            <label className="t1-ih-sort">
              <span className="sr-only">Sort</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                aria-label="Sort interviews"
              >
                <option value="score_desc">Highest {basis === "interview" ? "interview" : "competency"} score</option>
                <option value="score_asc">Lowest {basis === "interview" ? "interview" : "competency"} score</option>
                <option value="resume_desc">Highest resume match</option>
                <option value="duration_desc">Session duration</option>
                <option value="date_desc">Most recent</option>
              </select>
            </label>
          </div>
        </div>

        {metricSubFilter !== "all" && (
          <div className="t1-ih-subfilter" role="status">
            Showing <b>{filteredAndSortedInterviews.length}</b>{" "}
            {metricSubFilter === "needs_review"
              ? "that need review"
              : metricSubFilter === "verified"
                ? "verified"
                : metricSubFilter === "top_tier"
                  ? "strong match"
                  : "scored"}
            <button type="button" className="btn sm ghost" onClick={() => setMetricSubFilter("all")}>
              <X /> Clear
            </button>
          </div>
        )}

        {/* Feed */}
        {isLoading ? (
          <div className="t1-ih-empty">
            <p>Loading interviews…</p>
          </div>
        ) : isError ? (
          <div className="t1-ih-empty">
            <Alert style={{ width: 28, height: 28, color: "var(--crit)" }} />
            <h3>Couldn't load interviews</h3>
            <p>There was an error communicating with the server. Please try refreshing the page.</p>
          </div>
        ) : filteredAndSortedInterviews.length === 0 ? (
          <div className="t1-ih-empty">
            <Video style={{ width: 32, height: 32, color: "var(--ink-mute)" }} />
            <h3>{rawInterviews.length === 0 ? "No interviews yet" : "No matching interviews"}</h3>
            <p>
              {rawInterviews.length === 0
                ? "Nobody has taken an AI interview for this requirement yet. Send interview invites from the candidate pool to begin."
                : "No interview records match the current search or filters."}
            </p>
            {hasActiveFilters && (
              <button type="button" className="btn sm" onClick={clearAllFilters}>
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="t1-ih-tbl-wrap">
            <table className="t1-ih-tbl">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Status</th>
                  <th className="t1-ih-score-cell">Resume</th>
                  <th className="t1-ih-score-cell">Interview score</th>
                  <th className="t1-hide-md">Competencies</th>
                  <th>
                    <span className="sr-only">Action</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedInterviews.map((item) => (
                  <InterviewRow
                    key={item.candidacyId}
                    item={item}
                    backTo={backToUrl}
                    showRole={activeReqId === ALL}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer: count + honesty line + pagination */}
        <div className="t1-ih-foot">
          <span>
            Showing <b>{filteredAndSortedInterviews.length}</b> of <b>{rawInterviews.length}</b>
            {activeReqTitle ? ` · ${activeReqTitle}` : ""}
          </span>
          <span>Scores rank candidates; they never reject anyone automatically.</span>
        </div>
        {totalPages > 1 && (
          <div className="t1-ih-pager">
            <span>
              {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, totalInterviews)} of{" "}
              {totalInterviews}
            </span>
            <div>
              <button
                type="button"
                className="btn sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                <ArrowLeft /> Previous
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  type="button"
                  className={`btn sm ${pageNum === currentPage ? "primary" : "ghost"}`}
                  aria-current={pageNum === currentPage ? "page" : undefined}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              ))}
              <button
                type="button"
                className="btn sm"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next <ArrowRight />
              </button>
            </div>
          </div>
        )}
      </section>
      </div>

      <div className="t1-honesty">
        <b>Facial AI is proof only</b> — identity, liveness, injection &amp; second-person checks; it never scores
        face, tone, affect or personality. The engine ranks — a human always decides, and rejection is human-gated.
      </div>
    </div>
  );
}

/** "debugging" → "Debugging" (audit 17: mixed casing on competency chips). */
function sentenceCase(s: string): string {
  const t = s.trim();
  return t ? t[0]!.toUpperCase() + t.slice(1) : t;
}

/** Score band for rings/bars: ≥70 strong, 50–69 mid, <50 low. */
function scoreTone(n: number | null): "hi" | "mid" | "low" | "none" {
  if (n == null) return "none";
  return n >= 70 ? "hi" : n >= 50 ? "mid" : "low";
}

function ScoreRing({ value, label }: { value: number | null; label: string }): ReactNode {
  const tone = scoreTone(value);
  return (
    <span
      className={`t1-ring ${tone}`}
      style={{ ["--v" as string]: value ?? 0 }}
      role="img"
      aria-label={value == null ? `${label}: not scored yet` : `${label}: ${value}%`}
      title={value == null ? `${label}: not scored yet` : `${label}: ${value}%`}
    >
      <span>{value == null ? "—" : `${value}%`}</span>
    </span>
  );
}

/**
 * One status per row, with a one-line human reason under it (audit 17 / product review P0).
 * Wording never reads as an AI verdict: a failed integrity band asks for a human look at the
 * recording; it never says "do not advance".
 */
function rowStatus(item: InterviewItem): {
  pill: string;
  label: string;
  reason: string;
  icon: ReactNode;
  flag: boolean;
} {
  if (item.scoredAt == null) {
    return {
      pill: "pill pv",
      label: "In progress",
      reason: item.completedAt ? "Interview done — evaluating" : "Interview in progress",
      icon: null,
      flag: false,
    };
  }
  if (item.authenticityBand === "failed") {
    return {
      pill: "pill f",
      label: "Integrity issue",
      reason: "Integrity check failed — review the recording",
      icon: null,
      flag: true,
    };
  }
  if (item.authenticityBand === "needs_review") {
    return {
      pill: "pill r",
      label: "Needs review",
      reason: "Signals need a human check",
      icon: null,
      flag: false,
    };
  }
  if (item.authenticityBand === "verified") {
    return {
      pill: "pill v",
      label: "Verified",
      reason: "Identity & integrity passed",
      icon: null,
      flag: false,
    };
  }
  return { pill: "pill n", label: "Scored", reason: "Identity check was off", icon: null, flag: false };
}

function InterviewRow({
  item,
  backTo,
  showRole,
}: {
  item: InterviewItem;
  backTo: string;
  showRole: boolean;
}): ReactNode {
  const name = item.candidateName || item.candidateEmail || `Candidate ${item.candidacyId.slice(0, 6)}`;
  const initials = getInitials(name);
  const avatarColor = getAvatarColor(item.candidacyId);
  const status = rowStatus(item);
  const when = item.completedAt ?? item.scoredAt;
  const meta = [
    when ? relativeActivityDate(when) : null,
    item.sessionMinutes != null ? `${item.sessionMinutes} min` : null,
  ]
    .filter(Boolean)
    .join(", ");
  const reco = humaneRecommendation(item.recommendation);
  const scored = item.scoredAt != null;
  const href = `/app/review/${item.candidacyId}`;
  // Competency mini-bars: top 4 by score keeps the row one line tall; the full set is on the report.
  const comps = item.competencies.slice(0, 4);

  return (
    <tr className={status.flag ? "flag" : undefined}>
      <td className="t1-ih-who-cell" data-label="Candidate">
        <div className="t1-ih-who">
          <Link to={href} state={{ backTo }} className={`t1-av ${avatarColor}`} aria-hidden="true" tabIndex={-1}>
            {initials}
          </Link>
          <div style={{ minWidth: 0 }}>
            <Link to={href} state={{ backTo }} className="t1-ih-name" title={name}>
              {name}
            </Link>
            {item.candidateEmail && (
              <span className="t1-ih-email" title={item.candidateEmail}>
                {item.candidateEmail}
              </span>
            )}
            {showRole && (
              <Link
                to={`/app/requisitions/${item.requisitionId}`}
                className="t1-ih-role"
                title={`Open requirement: ${displayTitle(item.requisitionTitle)}`}
              >
                <Briefcase aria-hidden="true" />
                <span>{displayTitle(item.requisitionTitle)}</span>
              </Link>
            )}
          </div>
        </div>
      </td>
      <td data-label="Status">
        <span className={status.pill}>{status.label}</span>
        <div
          className="t1-ih-why"
          title={reco ? `AI note (surfaced for human review, not a decision): ${reco}` : undefined}
        >
          {status.reason}
        </div>
        {meta && <div className="t1-ih-when">{meta}</div>}
      </td>
      <td data-label="Resume" className="t1-ih-score-cell">
        <ScoreRing value={item.resumeScore} label="Resume match" />
      </td>
      <td data-label="Interview score" className="t1-ih-score-cell">
        <ScoreRing value={item.interviewScore} label="Interview score" />
      </td>
      <td className="t1-hide-md t1-ih-comps-cell" data-label="Competencies">
        {comps.length > 0 ? (
          <div className="t1-cg">
            {comps.map((c) => (
              <div
                key={c.id}
                className="t1-cb"
                title={`${sentenceCase(c.label)}: ${c.score == null ? "insufficient evidence" : `${c.score}%`}`}
              >
                <span>{sentenceCase(c.label)}</span>
                <div className={`t1-bt${c.score == null ? " neutral" : ""}`}>
                  {c.score != null && (
                    <i className={scoreTone(c.score)} style={{ width: `${Math.max(0, Math.min(100, c.score))}%` }} />
                  )}
                </div>
                <b className="tnum">{c.score ?? "—"}</b>
              </div>
            ))}
          </div>
        ) : (
          <span className="t1-ih-muted">
            {scored ? "No competency breakdown" : "Scores appear after the interview"}
          </span>
        )}
      </td>
      <td className="t1-ih-act">
        <Link
          to={href}
          state={{ backTo }}
          className={`btn sm ${scored ? "primary" : ""}`}
          aria-label={`${scored ? "Review" : "Track"} ${name}`}
        >
          {scored ? "Review" : "Track"}
        </Link>
      </td>
    </tr>
  );
}
